import React, {useEffect, useRef, useState} from 'react';
import * as pdfjs from 'pdfjs-dist';
import type {PDFDocumentProxy} from 'pdfjs-dist';
// CRA's webpack rewrites the standalone worker file into a CommonJS chunk
// ("require is not defined" inside the Worker), so the worker code is loaded
// on the main thread instead and pdf.js picks it up via globalThis.pdfjsWorker.
import 'pdfjs-dist/build/pdf.worker.entry';

interface Props {
  url: string;
  page: number;
  className?: string;
  onDocumentLoaded: (numPages: number) => void;
  onError: () => void;
}

const PdfPreview = React.memo(
  ({url, page, className, onDocumentLoaded, onError}: Props) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [doc, setDoc] = useState<PDFDocumentProxy>();

    useEffect(() => {
      let cancelled = false;
      const loadingTask = pdfjs.getDocument(url);
      loadingTask.promise
        .then(loadedDoc => {
          if (cancelled) return;
          setDoc(loadedDoc);
          onDocumentLoaded(loadedDoc.numPages);
        })
        .catch((error: unknown) => {
          // Surfaces the real cause (CORS, worker, mixed content…) for
          // machines where the canvas preview fails.
          console.error('PDF preview failed to load', url, error);
          if (!cancelled) onError();
        });
      return () => {
        cancelled = true;
        setDoc(undefined);
        loadingTask.destroy();
      };
    }, [url]);

    useEffect(() => {
      if (!doc) return;
      let cancelled = false;
      const pageNumber = Math.min(Math.max(1, page), doc.numPages);
      doc.getPage(pageNumber).then(pdfPage => {
        const canvas = canvasRef.current;
        if (cancelled || !canvas) return;
        const containerWidth = canvas.parentElement?.clientWidth || 380;
        const baseViewport = pdfPage.getViewport({scale: 1});
        const pixelRatio = window.devicePixelRatio || 1;
        const scale = (containerWidth / baseViewport.width) * pixelRatio;
        const viewport = pdfPage.getViewport({scale});
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const context = canvas.getContext('2d');
        if (!context) return;
        pdfPage.render({canvasContext: context, viewport});
      });
      return () => {
        cancelled = true;
      };
    }, [doc, page]);

    return <canvas ref={canvasRef} className={className} />;
  },
);

export default PdfPreview;
